'use server'

import { apiRequest, ApiResponse } from '../request'
import type {
    CreateRegistrationLinkData,
    RegistrationLink,
} from '../plans/plan-api'

export type { RegistrationLink, CreateRegistrationLinkData }

/**
 * Sub-plano do revendedor (filho do plano principal dele). Só aparece para o
 * revendedor que o criou. A distribuição é feita pelos links de cadastro.
 */
export interface SubPlan {
    id: number
    name: string
    description: string
    price: number
    recurrence: string
    features: Record<string, any>
    tierId: number | null
    additionalModules: Record<string, string[]>
    excludedModules: string[]
    ownerCompanyId: number | null
    parentPlanId: number | null
    createdAt: string
    _count?: { subscriptions: number }
}

export interface SubPlanData {
    name: string
    description: string
    price: number
    recurrence?: string
    features: Record<string, any>
    tierId?: number | null
    additionalModules?: Record<string, string[]>
    excludedModules?: string[]
}


export async function getSubPlans(): Promise<ApiResponse<SubPlan[]>> {
    return apiRequest<SubPlan[]>('resales', {})
}

export async function getSubPlan(id: number): Promise<ApiResponse<SubPlan>> {
    return apiRequest<SubPlan>(`resales/${id}`, {})
}

export async function createSubPlan(
    data: SubPlanData,
): Promise<ApiResponse<SubPlan>> {
    return apiRequest<SubPlan>('resales', { data }, 'post')
}

export async function updateSubPlan(
    id: number,
    data: SubPlanData,
): Promise<ApiResponse<SubPlan>> {
    return apiRequest<SubPlan>(`resales/${id}`, { data }, 'patch')
}

export async function removeSubPlan(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`resales/${id}`, {}, 'delete')
}

/**
 * Preço mínimo do sub-plano (centavos) para as quantidades de recursos informadas.
 * Calculado no backend de propósito — o revendedor não recebe multiplicador nem
 * preço unitário, só o valor mínimo final.
 */
export async function getResaleMinimumPrice(
    features: Record<string, number>
): Promise<ApiResponse<{ minPriceCents: number }>> {
    return apiRequest<{ minPriceCents: number }>(
        'resales/minimum-price',
        { data: { features } },
        'post'
    )
}

/** Módulos efetivos do plano principal — o sub-plano é limitado a este conjunto. */
export async function getResaleParentModules(): Promise<
    ApiResponse<Record<string, string[]>>
> {
    return apiRequest<Record<string, string[]>>('resales/parent-modules', {})
}

/**
 * Links de cadastro do sub-plano. Vários podem circular ao mesmo tempo — cada
 * um vale por um cadastro (ou por quantos vierem, se criado como ilimitado) e
 * criar um novo não invalida os anteriores.
 */
export async function getSubPlanRegistrationLinks(
    id: number,
): Promise<ApiResponse<RegistrationLink[]>> {
    return apiRequest<RegistrationLink[]>(
        `resales/${id}/registration-links`,
        {},
    )
}

export async function createSubPlanRegistrationLink(
    id: number,
    data: CreateRegistrationLinkData = {},
): Promise<ApiResponse<RegistrationLink>> {
    return apiRequest<RegistrationLink>(
        `resales/${id}/registration-links`,
        { data },
        'post',
    )
}

export async function revokeSubPlanRegistrationLink(
    id: number,
    linkId: number,
): Promise<ApiResponse<RegistrationLink>> {
    return apiRequest<RegistrationLink>(
        `resales/${id}/registration-links/${linkId}/revoke`,
        {},
        'post',
    )
}
